import React, { useRef } from "react";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import { Button } from "@/components/ui/button";
import {
  Download,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  RefreshCw,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RoutesLink } from "@/utility/RouteLinks";
import useBulkImport from "./useBulkImport";
import ImportPreviewTable from "./ImportPreviewTable";

const BulkImportEmployee: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    step,
    headers,
    validRows,
    invalidRows,
    isAllValid,
    isDownloading,
    isUploading,
    isImporting,
    uploadedFile,
    downloadTemplate,
    uploadAndPreview,
    updateRow,
    submitImport,
    reset,
  } = useBulkImport();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadAndPreview(file);
    }
    // Reset file input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImport = async () => {
    const success = await submitImport();
    if (success) {
      navigate(RoutesLink.superAdminEmplyoeeOnboard);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <MainHeading
          title="Bulk Import Employees"
          isBackButtonVisible={true}
        />

        {/* ── Step 1: Upload Section ── */}
        {step === "upload" && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Step indicator */}
            <div className="px-6 py-4 bg-gradient-to-r from-primary/5 to-transparent border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                  1
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800">
                    Download Template & Upload Data
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Download the Excel template, fill in employee data, then upload it
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Download template */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 bg-blue-50/50 rounded-xl border border-blue-100">
                <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                  <FileSpreadsheet className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-slate-800">
                    Step 1: Download Template
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">
                    Download the Excel template file, fill it with employee data, and keep the column headers unchanged.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="bg-white gap-2 shrink-0"
                  onClick={downloadTemplate}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <RefreshCw size={16} className="animate-spin" />
                  ) : (
                    <Download size={16} />
                  )}
                  Download Template
                </Button>
              </div>

              {/* Upload file */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 bg-emerald-50/50 rounded-xl border border-emerald-100">
                <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                  <Upload className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-slate-800">
                    Step 2: Upload Filled Template
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">
                    Upload your filled Excel file (.xlsx, .xls, .csv) to preview the data before importing.
                  </p>
                </div>
                <div className="shrink-0">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                  <Button
                    className="gap-2"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <RefreshCw size={16} className="animate-spin" />
                    ) : (
                      <Upload size={16} />
                    )}
                    {isUploading ? "Uploading..." : "Upload File"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Preview Section ── */}
        {step === "preview" && (
          <div className="space-y-4">
            {/* Preview header bar */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-primary/5 to-transparent border-b border-slate-100">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                      2
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-800">
                        Preview & Validate Data
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {uploadedFile?.name && (
                          <span className="font-medium text-slate-600">
                            File: {uploadedFile.name} •{" "}
                          </span>
                        )}
                        Review the data below. Fix any invalid rows before importing.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      className="gap-2 text-sm"
                      onClick={reset}
                    >
                      <ArrowLeft size={14} />
                      Upload Different File
                    </Button>
                    <Button
                      className="gap-2 text-sm"
                      onClick={handleImport}
                      disabled={!isAllValid || isImporting}
                    >
                      {isImporting ? (
                        <RefreshCw size={14} className="animate-spin" />
                      ) : (
                        <CheckCircle2 size={14} />
                      )}
                      {isImporting ? "Importing..." : "Import All"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Table */}
            <ImportPreviewTable
              headers={headers}
              validRows={validRows}
              invalidRows={invalidRows}
              onUpdateRow={updateRow}
            />

            {/* Bottom action bar */}
            {isAllValid && validRows.length > 0 && (
              <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                  <CheckCircle2 size={18} />
                  All {validRows.length} rows are valid and ready to import!
                </div>
                <Button
                  className="gap-2"
                  onClick={handleImport}
                  disabled={isImporting}
                >
                  {isImporting ? (
                    <RefreshCw size={14} className="animate-spin" />
                  ) : (
                    <CheckCircle2 size={14} />
                  )}
                  {isImporting ? "Importing..." : "Import All Employees"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BulkImportEmployee;
